import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import { getBookingCalendar } from '../../api/bookingCalendarApi';
import AdminBookingMonthGrid from './AdminBookingMonthGrid';
import { formatBookingDate, statusBadgeClass } from './adminBookingCalendarUtils';
import '../../styles/admin-manage.scss';
import './AdminManageToolbar.scss';
import './BookingCalendar.scss';

export default function ManageGroupTrips() {
  const location = useLocation();
  const history = useHistory();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [calendarSummary, setCalendarSummary] = useState(null);
  const [monthItems, setMonthItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthDetailLoading, setMonthDetailLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('needing_action') === '1') {
      history.replace({ pathname: location.pathname, search: '' });
    }
  }, [location.search, location.pathname, history]);

  const fetchCalendarSummary = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await getBookingCalendar({ year });
      setCalendarSummary(res.data);
    } catch (e) {
      console.warn('ManageGroupTrips calendar error', e);
      setLoadFailed(true);
      setCalendarSummary(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const fetchMonthDetail = useCallback(async (month) => {
    if (!month) {
      setMonthItems([]);
      return;
    }
    setMonthDetailLoading(true);
    try {
      const res = await getBookingCalendar({ year, month });
      setMonthItems(res.data?.group_items || []);
    } catch (e) {
      console.warn('ManageGroupTrips month detail error', e);
      setMonthItems([]);
    } finally {
      setMonthDetailLoading(false);
    }
  }, [year]);

  useEffect(() => {
    setSelectedMonth(null);
    setMonthItems([]);
    fetchCalendarSummary();
  }, [fetchCalendarSummary]);

  useEffect(() => {
    fetchMonthDetail(selectedMonth);
  }, [selectedMonth, fetchMonthDetail]);

  const handleYearChange = (nextYear) => {
    setYear(nextYear);
    setSelectedMonth(null);
    setMonthItems([]);
  };

  const listTitle = selectedMonth
    ? moment(`${year}-${String(selectedMonth).padStart(2, '0')}-01`).format('MMMM YYYY')
    : null;

  return (
    <div className="container py-4 admin-manage-page booking-cal">
      <Link to="/admin" className="btn btn-outline-secondary btn-sm mb-3">← Dashboard</Link>
      <header className="admin-manage-page__header d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h2 className="mb-1">Manage Group Trips</h2>
          <p className="admin-manage-page__subtitle mb-0">
            Click a month to see trips. Orange counts are waiting on you — payments, vendor, or close-out.
          </p>
        </div>
        <Link to="/admin/group_tours" className="btn btn-outline-secondary btn-sm">All group tours</Link>
      </header>

      <div className="booking-cal__legend mb-3 mt-3">
        <span><span className="badge badge-warning">Waiting</span> needs you</span>
        <span><span className="badge badge-success">Paid</span> done</span>
        <span><span className="badge badge-secondary">Cancelled</span> closed</span>
      </div>

      {loadFailed && (
        <p className="text-muted mb-3">Could not load trips right now.</p>
      )}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading…</span></div>
        </div>
      )}

      {!loading && !loadFailed && calendarSummary && (
        <AdminBookingMonthGrid
          summary={calendarSummary}
          year={year}
          onYearChange={handleYearChange}
          selectedMonth={selectedMonth}
          onMonthSelect={setSelectedMonth}
          bookingType="group"
        />
      )}

      {!loading && !loadFailed && !selectedMonth && (
        <p className="text-muted text-center mt-4 mb-0">Pick a month to open the trip list.</p>
      )}

      {selectedMonth && monthDetailLoading && (
        <div className="text-center py-4 mt-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="sr-only">Loading…</span></div>
        </div>
      )}

      {selectedMonth && !monthDetailLoading && monthItems.length === 0 && (
        <section className="booking-cal__detail mt-4">
          <h3 className="h6 mb-3 text-muted">{listTitle}</h3>
          <p className="text-muted mb-0">No group trips this month.</p>
        </section>
      )}

      {selectedMonth && !monthDetailLoading && monthItems.length > 0 && (
        <section className="booking-cal__detail mt-4" aria-label={listTitle}>
          <h3 className="h5 mb-3">{listTitle} ({monthItems.length})</h3>
          <div className="table-responsive">
            <table className="table table-sm table-hover admin-manage-table booking-cal__table">
              <thead>
                <tr>
                  <th>Trip</th>
                  <th>Start date</th>
                  <th>People</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {monthItems.map((item) => (
                  <tr key={item.idx} className={item.status === 'waiting' ? 'admin-manage-table__waiting' : undefined}>
                    <td>
                      <strong>{item.name}</strong>
                      {item.journey_slug && <div className="text-muted small">{item.journey_slug}</div>}
                    </td>
                    <td>{formatBookingDate(item.start_date)}</td>
                    <td>{item.paid_people_count} / min {item.min_group_size}</td>
                    <td><span className={`badge badge-${statusBadgeClass(item.status)}`}>{item.status_label}</span></td>
                    <td className="text-right">
                      <Link to={`/admin/group_tour_details/${item.idx}`} className="btn btn-sm btn-primary">Manage</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
